import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';

import { DEFAULT_LOCALE } from '../../shared/i18n/i18n.config';
import { StoreService } from '../../shared/services/store.service';
import { AppState } from '../../shared/store/app-state';
import { getUserCountry } from '../../shared/store/selectors/login';
import { MANDATORY_LEGAL_DOCUMENTS } from '../MandatoryLegalDocuments';
import { LegalDocument } from '../model/LegalDocument';
import { LegalDocumentApproval } from '../model/LegalDocumentApproval';
import { LEGAL_DOCUMENTS_TYPES } from '../model/LegalDocumentType';

import { HttpLegalDocumentGateway } from './HttpLegalDocumentGateway';
import { fromApprovedApiResponse, fromToApproveApiResponse } from './legalDocumentMapping';

@Injectable()
export class LegalDocumentService {
    constructor(
        @Inject('LegalDocumentGateway') private legalDocumentGateway: HttpLegalDocumentGateway,
        private translateService: TranslateService,
        private store: StoreService<AppState>,
    ) {}

    getLastLegalDocumentsApproval(): Observable<LegalDocumentApproval> {
        return this.store.select(getUserCountry).pipe(
            switchMap((countryCode: string) => this.legalDocumentGateway.getApprovals(countryCode)),
            map((approvals) => {
                const currentLang = this.getCurrentLanguage();
                const lastApproval = approvals[approvals.length - 1];
                const approvedDocuments = lastApproval?.legalDocuments
                    .filter((document) => Object.values(LEGAL_DOCUMENTS_TYPES).includes(document.documentType))
                    .map((document) => fromApprovedApiResponse(document, currentLang));

                return lastApproval ? { approvalDate: lastApproval.creationTime, approvedDocuments } : null;
            }),
        );
    }

    getDocumentToApprove(): Observable<LegalDocument[]> {
        return this.store.select(getUserCountry).pipe(
            switchMap((countryCode: string) => this.legalDocumentGateway.getDocumentToApprove(countryCode)),
            map((documents) => {
                const currentLang = this.getCurrentLanguage();
                return documents
                    .filter((document) => MANDATORY_LEGAL_DOCUMENTS.includes(document.documentType))
                    .map((document) => fromToApproveApiResponse(document, currentLang));
            }),
        );
    }

    approveLegalDocuments(): Observable<void> {
        return this.store
            .select(getUserCountry)
            .pipe(switchMap((countryCode: string) => this.legalDocumentGateway.approveLegalDocuments(countryCode)));
    }

    private getCurrentLanguage(): string {
        const locale = this.translateService.getCurrentLang() || DEFAULT_LOCALE;
        return locale.split('-')[0];
    }
}
